import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Zap, X } from "lucide-react";

interface UnlimitedDevLightboxProps {
  open: boolean;
  onClose: () => void;
  paymentLink?: string;
}

const UnlimitedDevLightbox = ({ open, onClose, paymentLink }: UnlimitedDevLightboxProps) => {
  const defaultPaymentLink = "https://connect.intuit.com/pay/Venture24/scs-v1-810fb25f51ee4adaa74f3c37c01811e3ed02c9b24e2a4fa3aa6672960c45341ae3e25515e44448fbb25170fc5c0562e6?locale=EN_US";
  
  const handleGetAccess = () => {
    onClose();
    window.location.href = paymentLink || defaultPaymentLink;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) onClose();
    }}>
      <DialogContent className="sm:max-w-[600px] overflow-auto max-h-[90vh]">
        <DialogClose asChild onClick={onClose} className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
          <button>
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogClose>
        
        <DialogHeader className="text-center">
          <DialogTitle className="text-2xl sm:text-3xl font-bold text-center mb-2">Unlimited AI App Development</DialogTitle>
          <DialogDescription className="text-center text-sm text-gray-500">
            Get unlimited app development with no extra fees
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
            <Zap className="h-8 w-8 text-orange-600" />
          </div>
          
          <div className="text-center mb-6">
            <div className="text-4xl font-bold mb-2">$599<span className="text-lg font-normal text-gray-500">/month</span></div>
            <p className="text-gray-600">Get unlimited AI app builds, updates, and expert support—no extra fees, no limits</p>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <div className="h-5 w-5 mt-0.5 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p>Unlimited app builds - create as many apps as you want</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="h-5 w-5 mt-0.5 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p>Ongoing updates & improvements to your applications</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="h-5 w-5 mt-0.5 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p>Priority support access - get help when you need it</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="h-5 w-5 mt-0.5 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p>Monthly strategy sessions with app development experts</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="h-5 w-5 mt-0.5 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p>Custom feature development for your specific needs</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="h-5 w-5 mt-0.5 flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p>Publishing assistance - get your apps to market faster</p>
            </div>
          </div>
          
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
              onClick={handleGetAccess}
            >
              Get Unlimited Access
            </Button>
            <Button 
              variant="outline" 
              onClick={onClose} 
              className="w-full sm:w-auto"
            >
              Not Now
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UnlimitedDevLightbox;
